import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowRightOutlined } from '@ant-design/icons';
import { MONO_FONT, RADIUS } from '../../../../components/ui/tokens';
import { getPlatformDisplayName, getPlatformLogo } from '../../../../constants/platforms';
import { Eyebrow, ink } from '../../../mcp/SignalDesk';
import { risk_type_display_map } from '../../CopyTradingTableSection';
import { SimulateAccount, useSimulateMeta } from '../SimulateTradeContext';
import { SIMULATE_COPY } from '../content';

const LOGO_SIZE_PX = 18;
const { route } = SIMULATE_COPY;

const AccountTag = ({ label, account }: { label: string; account?: SimulateAccount }) => {
  const theme = useTheme();
  const logo = account && getPlatformLogo(account.platform, theme.palette.mode === 'dark');
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Eyebrow>{label}</Eyebrow>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {logo && (
          <Box
            component="img"
            src={logo}
            alt={getPlatformDisplayName(account.platform)}
            sx={{ width: LOGO_SIZE_PX, height: LOGO_SIZE_PX, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        <Typography noWrap sx={{ fontFamily: MONO_FONT, fontSize: 13, fontWeight: 600 }}>
          {account?.accountNumber || '—'}
        </Typography>
      </Stack>
    </Stack>
  );
};

/** Which link is being simulated: master account → slave account, with the slave's risk type. */
export const SimulateRoute = () => {
  const theme = useTheme();
  const { masterAccount, slaveAccount, getSlaveConfigDraft } = useSimulateMeta();
  const riskLabel: string | undefined = risk_type_display_map[Number(getSlaveConfigDraft().riskType)];

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: RADIUS.card,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.02)
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
        <AccountTag label={route.master} account={masterAccount} />
        <Box aria-label={route.copiesTo} sx={{ color: ink(theme), display: 'flex', fontSize: 14, pt: 2 }}>
          <ArrowRightOutlined />
        </Box>
        <AccountTag label={route.slave} account={slaveAccount} />
      </Stack>
      {riskLabel && (
        <Typography
          variant="caption"
          sx={{
            px: 1.25,
            py: 0.5,
            flexShrink: 0,
            fontWeight: 600,
            borderRadius: RADIUS.pill,
            color: ink(theme),
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)
          }}
        >
          {riskLabel}
        </Typography>
      )}
    </Stack>
  );
};
